import z from 'zod';
import {
  AllCharLabelSchema,
  CognitoUserIdSchema,
  UUIDSchema,
  UnixTimestampSchema,
} from '../common/schemas';
import { DBBigIntSchema, DBJsonObjectSchema, DBTimestampSchema } from '../common/sql';
import { QUESTION_DIFFICULTY_MAX, QUESTION_DIFFICULTY_MIN } from '../questions/constants';
import {
  SOURCE_DOCUMENT_EARLIEST_YEAR,
  SOURCE_DOCUMENT_LATEST_YEAR,
} from '../sourceDocuments/schemas';
import { VocabularyTermCodeSchema } from '../vocabularies/schemas';
import {
  QUESTION_SET_BLUEPRINT_VERSION,
  QUESTION_SET_CODE_PATTERN,
  QUESTION_SET_MAX_COUNT,
  QUESTION_SET_MAX_DIFFICULTY_RANGES,
  QUESTION_SET_MAX_DIRECTIVE_VERBS,
  QUESTION_SET_MAX_DURATION_MINUTES,
  QUESTION_SET_MAX_QUESTION_TYPES,
  QUESTION_SET_MAX_SOURCE_FILTER_INSTITUTIONS,
  QUESTION_SET_MAX_SOURCE_FILTERS,
  QUESTION_SET_MAX_SOURCE_SETS,
  QUESTION_SET_MAX_TOPIC_GROUPS,
  QUESTION_SET_MAX_TOPICS,
  QUESTION_SET_MIN_DURATION_MINUTES,
} from './constants';
import {
  QuestionSetDistribution,
  QuestionSetPreviouslyUsed,
  QuestionSetSourceFilterKind,
  QuestionSetSourceFilterMode,
  QuestionSetStatus,
  QuestionSetVisibility,
} from './enums';

const CODE_MESSAGE = 'A set code is six characters from ACEFHJKMNPRTUWXY23456789';

/**
 * DB Schemas
 *
 * String bounds match the tables' CHECK constraints. A tighter bound would log a
 * warning for rows the database accepts.
 */

export const DBQuestionSetStatusSchema = z.enum(QuestionSetStatus);
export const DBQuestionSetCodeSchema = z.string().regex(QUESTION_SET_CODE_PATTERN, CODE_MESSAGE);
export const DBQuestionSetTitleSchema = AllCharLabelSchema.min(1);
export const DBQuestionSetDescriptionSchema = AllCharLabelSchema.nullable();
export const DBQuestionSetVisibilitySchema = z.enum(QuestionSetVisibility);
export const DBQuestionSetDurationMinutesSchema = z
  .number()
  .int()
  .min(QUESTION_SET_MIN_DURATION_MINUTES)
  .max(QUESTION_SET_MAX_DURATION_MINUTES)
  .nullable();

export const DBQuestionSetSchema = z.object({
  id: UUIDSchema,
  code: DBQuestionSetCodeSchema,
  title: DBQuestionSetTitleSchema,
  description: DBQuestionSetDescriptionSchema,
  status: DBQuestionSetStatusSchema,
  visibility: DBQuestionSetVisibilitySchema,
  durationMinutes: DBQuestionSetDurationMinutesSchema,
  blueprint: DBJsonObjectSchema,
  randomSeed: DBBigIntSchema.nullable(),
  composedBy: CognitoUserIdSchema.nullable(),
  publishedBy: CognitoUserIdSchema.nullable(),
  publishedAt: DBTimestampSchema.nullable(),
  createdAt: DBTimestampSchema,
  updatedAt: DBTimestampSchema.nullable(),
});

export const DBQuestionSetItemSchema = z.object({
  id: UUIDSchema,
  setId: UUIDSchema,
  questionId: UUIDSchema,
  position: z.number().int().min(0),
  sectionLabel: AllCharLabelSchema.nullable(),
  displayNumber: AllCharLabelSchema.nullable(),
  addedBy: CognitoUserIdSchema.nullable(),
  createdAt: DBTimestampSchema,
});

export const DBQuestionSetTypeTagSchema = z.object({
  setId: UUIDSchema,
  setTypeTagId: UUIDSchema,
  createdAt: DBTimestampSchema,
});

/**
 * A row of the `question_set_marks` view. `marks` is null while any leaf has no
 * marks.
 */
export const DBQuestionSetMarksSchema = z.object({
  setId: UUIDSchema,
  items: z.number().int().min(0),
  marks: z.number().int().min(0).nullable(),
});

/** A row returned by the `public.composable_units` function. */
export const DBComposableUnitSchema = z.object({
  questionId: UUIDSchema,
  rootQuestionId: UUIDSchema,
  marks: z.number().int().min(0),
  leafCount: z.number().int().positive(),
});

/**
 * Client Schemas
 */

export const QuestionSetIdSchema = UUIDSchema;
export const QuestionSetItemIdSchema = UUIDSchema;

export const QuestionSetStatusSchema = z.enum(QuestionSetStatus).openapi({
  description: 'Lifecycle status of the set',
  example: QuestionSetStatus.Draft,
});

export const QuestionSetVisibilitySchema = z.enum(QuestionSetVisibility).openapi({
  description:
    'Who the set is for. A public set is sat by many classes, so while it is drafted or ' +
    'published no other set may hold its questions; a private set shares them freely.',
  example: QuestionSetVisibility.Private,
});

export const QuestionSetCodeSchema = DBQuestionSetCodeSchema.openapi({
  description:
    'The six-character code a class types to open the set. The alphabet leaves out characters ' +
    'that look alike when handwritten or projected.',
  example: 'KX7M2A',
});

export const QuestionSetCodeLookupSchema = z
  .string()
  .max(32)
  .transform(value => value.toUpperCase().replace(/[\s-]/g, ''))
  .pipe(DBQuestionSetCodeSchema)
  .openapi({
    description: 'A set code in any case, with or without spaces or dashes',
    example: 'kx7m 2a',
  });

export const QuestionSetTitleSchema = DBQuestionSetTitleSchema.max(512).openapi({
  description: 'The name the set is listed under',
  example: 'Week 3 projectile motion quiz',
});

export const QuestionSetDescriptionSchema = AllCharLabelSchema.max(4096).nullable().openapi({
  description: 'A note for whoever gives the set',
  example: 'Ten multiple-choice questions on resolving launch velocity.',
});

export const QuestionSetDurationMinutesSchema = DBQuestionSetDurationMinutesSchema.openapi({
  description:
    'How long the set runs, in whole minutes, where the tutor has chosen. A live quiz’s timer ' +
    'starts at it. Null runs the set for the time its questions are expected to take.',
  example: 10,
});

export const QuestionSetRandomSeedSchema = z.number().int().min(0).nullable().openapi({
  description: 'The seed used for the random draw, kept so the composition can be explained',
  example: 1840287361,
});

const withOrderedBounds = <
  T extends z.ZodObject<{ min: z.ZodOptional<z.ZodNumber>; max: z.ZodOptional<z.ZodNumber> }>,
>(
  bounds: T
) =>
  bounds.refine(
    value => value.min === undefined || value.max === undefined || value.min <= value.max,
    { message: 'min must not exceed max', path: ['min'] }
  );

/** Inclusive bounds on a scale's value; either end may be open. */
export const QuestionSetScaleBoundsSchema = withOrderedBounds(
  z.object({ min: z.number().optional(), max: z.number().optional() })
);

const DifficultySchema = z.number().min(QUESTION_DIFFICULTY_MIN).max(QUESTION_DIFFICULTY_MAX);

/** Inclusive bounds on a leaf's computed difficulty out of 10; either end may be open. */
export const QuestionSetDifficultyRangeSchema = withOrderedBounds(
  z.object({ min: DifficultySchema.optional(), max: DifficultySchema.optional() })
);

/** Inclusive bounds on a unit's total marks; either end may be open. */
export const QuestionSetMarksBoundsSchema = withOrderedBounds(
  z.object({ min: z.number().int().min(0).optional(), max: z.number().int().min(0).optional() })
);

const SourceFilterModeSchema = z.enum(QuestionSetSourceFilterMode).openapi({
  description:
    'Include keeps a unit only where one of its papers meets every include filter. Exclude ' +
    'leaves out a unit any of whose papers it matches.',
});

const SourceFilterYearSchema = z
  .number()
  .int()
  .min(SOURCE_DOCUMENT_EARLIEST_YEAR)
  .max(SOURCE_DOCUMENT_LATEST_YEAR);

/** Matches a paper set by any of the institutions it names. */
export const QuestionSetInstitutionFilterSchema = z.object({
  kind: z.literal(QuestionSetSourceFilterKind.Institution),
  mode: SourceFilterModeSchema,
  institutionIds: z
    .array(UUIDSchema)
    .min(1)
    .max(QUESTION_SET_MAX_SOURCE_FILTER_INSTITUTIONS)
    .openapi({ description: 'The institutions whose papers the filter matches' }),
});

/** Matches a paper written within inclusive bounds on its year; either end may be open. */
export const QuestionSetYearFilterSchema = z
  .object({
    kind: z.literal(QuestionSetSourceFilterKind.Year),
    mode: SourceFilterModeSchema,
    min: SourceFilterYearSchema.optional(),
    max: SourceFilterYearSchema.optional(),
  })
  .refine(filter => filter.min !== undefined || filter.max !== undefined, {
    message: 'Name a year at one end at least',
    path: ['min'],
  })
  .refine(
    filter => filter.min === undefined || filter.max === undefined || filter.min <= filter.max,
    { message: 'min must not exceed max', path: ['min'] }
  );

/** A filter on the papers a unit was found in. A paper not recording what it reads never matches. */
export const QuestionSetSourceFilterSchema = z.discriminatedUnion('kind', [
  QuestionSetInstitutionFilterSchema,
  QuestionSetYearFilterSchema,
]);

/** The criteria a set was composed from, stored with the set. */
export const QuestionSetBlueprintSchema = z
  .object({
    version: z.literal(QUESTION_SET_BLUEPRINT_VERSION).default(QUESTION_SET_BLUEPRINT_VERSION),
    subjectId: UUIDSchema.openapi({ description: 'The subject of every drawn unit' }),
    topicIds: z.array(UUIDSchema).max(QUESTION_SET_MAX_TOPICS).openapi({
      description:
        'A drawn unit has at least one topic from this list or from the groups in topicGroupIds.',
    }),
    topicGroupIds: z.array(UUIDSchema).max(QUESTION_SET_MAX_TOPIC_GROUPS).openapi({
      description: 'Topic groups, each standing for every topic in the group or its subgroups',
    }),
    excludedTopicGroupIds: z
      .array(UUIDSchema)
      .max(QUESTION_SET_MAX_TOPIC_GROUPS)
      .optional()
      .openapi({
        description:
          'Topic groups the students have not learnt. A drawn unit has no topic held only by ' +
          'these groups and their subgroups within their syllabus, unless the blueprint names it.',
      }),
    questionTypeCodes: z
      .array(VocabularyTermCodeSchema)
      .min(1)
      .max(QUESTION_SET_MAX_QUESTION_TYPES)
      .openapi({
        description: 'Every leaf of a drawn unit has one of these question types',
        example: ['multiple_choice'],
      }),
    directiveVerbCodes: z
      .array(VocabularyTermCodeSchema)
      .max(QUESTION_SET_MAX_DIRECTIVE_VERBS)
      .optional()
      .openapi({ description: 'At least one part of a drawn unit has one of these verbs' }),
    scales: z
      .record(VocabularyTermCodeSchema, QuestionSetScaleBoundsSchema)
      .optional()
      .openapi({
        description: 'Bounds by scale code. Every leaf must score within each bound.',
        example: { cognitive_demand: { min: 2, max: 4 } },
      }),
    difficultyRanges: z
      .array(QuestionSetDifficultyRangeSchema)
      .min(1)
      .max(QUESTION_SET_MAX_DIFFICULTY_RANGES)
      .optional()
      .openapi({
        description:
          'Every leaf of a drawn unit has a difficulty out of 10 within one of these ranges. ' +
          'A leaf with no difficulty is within none.',
        example: [
          { min: 1, max: 2.9 },
          { min: 7, max: 10 },
        ],
      }),
    marks: QuestionSetMarksBoundsSchema.optional().openapi({
      description: 'Bounds on a unit’s total marks',
    }),
    maxOptions: z.number().int().positive().max(26).optional().openapi({
      description: 'The most options any leaf may have. The answer sheet fits five.',
      example: 5,
    }),
    count: z.number().int().min(1).max(QUESTION_SET_MAX_COUNT).openapi({
      description: 'How many units to draw',
      example: 10,
    }),
    singlePart: z.boolean().openapi({
      description: 'Whether every drawn unit must have exactly one leaf',
      example: true,
    }),
    sourceSetIds: z.array(UUIDSchema).max(QUESTION_SET_MAX_SOURCE_SETS).optional().openapi({
      description: 'Draw only from these published sets',
    }),
    sourceFilters: z
      .array(QuestionSetSourceFilterSchema)
      .max(QUESTION_SET_MAX_SOURCE_FILTERS)
      .optional()
      .openapi({
        description:
          'Filters on the papers a unit’s root question was found in, by the institution ' +
          'that set each paper and the year it was written',
      }),
    previouslyUsed: z
      .enum(QuestionSetPreviouslyUsed)
      .default(QuestionSetPreviouslyUsed.Include)
      .openapi({ description: 'What to do with a unit already in a published set' }),
    distribution: z
      .enum(QuestionSetDistribution)
      .default(QuestionSetDistribution.Random)
      .openapi({ description: 'How the units are drawn from the pool' }),
  })
  .refine(blueprint => blueprint.topicIds.length + blueprint.topicGroupIds.length > 0, {
    message: 'Name at least one topic or topic group',
    path: ['topicIds'],
  });

export const QuestionSetSchema = z.object({
  id: QuestionSetIdSchema,
  code: QuestionSetCodeSchema,
  title: QuestionSetTitleSchema,
  description: QuestionSetDescriptionSchema,
  status: QuestionSetStatusSchema,
  visibility: QuestionSetVisibilitySchema,
  durationMinutes: QuestionSetDurationMinutesSchema,
  blueprint: QuestionSetBlueprintSchema,
  randomSeed: QuestionSetRandomSeedSchema,
  composedBy: CognitoUserIdSchema.nullable(),
  publishedBy: CognitoUserIdSchema.nullable(),
  publishedAt: UnixTimestampSchema.nullable(),
  createdAt: UnixTimestampSchema,
  updatedAt: UnixTimestampSchema.nullable(),
});

export const QuestionSetItemDisplayNumberSchema = AllCharLabelSchema.max(32).nullable().openapi({
  description: 'The unit’s number in this set, also used for its row on the answer sheet',
  example: '3',
});

export const QuestionSetItemSchema = z.object({
  id: QuestionSetItemIdSchema,
  setId: QuestionSetIdSchema,
  questionId: UUIDSchema,
  position: z.number().int().min(0),
  sectionLabel: AllCharLabelSchema.max(64).nullable(),
  displayNumber: QuestionSetItemDisplayNumberSchema,
  addedBy: CognitoUserIdSchema.nullable(),
  createdAt: UnixTimestampSchema,
});

export const QuestionSetTypeTagSchema = z.object({
  setId: QuestionSetIdSchema,
  setTypeTagId: UUIDSchema,
  createdAt: UnixTimestampSchema,
});

export const QuestionSetMarksSchema = z.object({
  setId: QuestionSetIdSchema,
  items: z.number().int().min(0).openapi({ description: 'How many units the set holds' }),
  marks: z
    .number()
    .int()
    .min(0)
    .nullable()
    .openapi({ description: 'Total marks, or null while any leaf has no marks' }),
});

/** A unit composition can draw: a published root question, or a part usable on its own. */
export const ComposableUnitSchema = z.object({
  questionId: UUIDSchema,
  rootQuestionId: UUIDSchema.openapi({
    description: 'The root question of the unit’s tree; the unit’s own id when it is a root',
  }),
  marks: z.number().int().min(0),
  leafCount: z.number().int().positive(),
});
